import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { UserPlus, ChevronDown, ExternalLink, Loader2, Search, CalendarClock, X } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { asList } from '../../lib/chart-helpers.js'
import { usePersistedState } from '../../lib/persist.js'
import { useTypeahead } from '../../lib/useTypeahead.js'
import { apiErrorText } from '../../lib/apiError.js'
import { apptStatusInfo } from '../../data/appointmentStatuses.js'
import { buildRows, pivot, filterRows, fmtDay, typeOptions, bkkTodayISO, GROUPERS, NO_APPT, NO_APPT_LABEL } from '../../lib/newcase.js'
import { Card, DateRange, Segmented, Notice, fmt, INPUT } from '../statistic/statShared.jsx'

/* -------------------------------------------------------------------------- */
/* เคสนัดใหม่ประจำวัน — the HNs opened on a day, and the appointment each one    */
/* was opened for.                                                            */
/*                                                                            */
/* Source: GET /api/newcase → Xano /report/newcase_daily (api 649, claude     */
/* group). It returns { patients, appointments } — every patient whose        */
/* created_at falls inside the Bangkok window, plus a FLAT list of those       */
/* patients' appointments. One row here = one new HN; its appointment is the   */
/* EARLIEST one (the server sorts by date asc), and any others are counted as  */
/* "+N นัด".                                                                   */
/*                                                                            */
/* Branch / appointment-type filtering and every grouping axis run CLIENT-side */
/* off that one payload, so switching จัดกลุ่มตาม never refetches.              */
/*                                                                            */
/* ⚠️ Deliberately different from the old Bubble chart (report/chart/newcase): */
/* that one DROPS an appointment whose ที่มา is ต่อเนื่อง or Recall, and drops  */
/* branch 0 and branch 9 (Tooth Taxi). Nothing is dropped here — a new HN      */
/* whose source was mis-keyed as ต่อเนื่อง is a data-entry error worth seeing,  */
/* not a case worth hiding, and it is visible the moment you group by ที่มา.   */
/* So totals here read slightly HIGHER than the old chart. Measured 2026-09-07:*/
/* ต่อเนื่อง/Recall is ~4% of new HNs' first appointments.                      */
/* -------------------------------------------------------------------------- */

/* Chart colour. ONE series (a count of new cases) split across categories, so
 * every bar takes the SAME brand blue — colouring nominal bars by their value
 * would re-encode what the bar length already shows and spend the identity
 * channel on nothing. (Was a per-index rainbow; that read as 7 different
 * things being compared.) brand-600 on white = 5.17:1, well over the 3:1
 * non-text floor.
 * ยังไม่มีนัด is the exception — it is a GAP, not a category, so it stays a
 * neutral slate that recedes from the blue. slate-500 = 4.76:1. */
const BAR = '#2563eb'      // brand-600, the app's own blue
const BAR_GAP = '#64748b'  // slate-500 — ยังไม่มีนัด

/* The longest window the picker will offer. Deliberately tighter than the
 * endpoint's own 92-day precondition — see the note at the DateRange below. */
const NEWCASE_MAX_DAYS = 60

const GROUP_OPTS = [
  { v: 'type', label: 'ประเภทนัดหมาย' },
  { v: 'from', label: 'ที่มา' },
  { v: 'branch', label: 'สาขา' },
  { v: 'date', label: 'วันที่' },
]

/* ── small bits ────────────────────────────────────────────────────────────── */
function Select({ value, onChange, children, className = '' }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} className={`${INPUT} ${className}`}>{children}</select>
}

// ประเภทนัดหมาย picker — searchable, and TWO levels deep: pick a whole family
// (GP) or one catalogue entry inside it (GP › ตรวจฟัน). Families lead; their
// entries are indented under them.
//
// A sibling of the flat SearchSelect in reports/Upsale.jsx. Not shared with it:
// that one is flat and draws an employee Avatar, this one groups and counts.
// If a third appears, extract them both rather than making a third copy.
function TypeSelect({ value, onChange, groups, gapCount, className = '' }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const inputRef = useRef(null)
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus() }, [open])

  const needle = q.trim().toLowerCase()
  // A family stays visible when IT matches (with all its entries) or when any
  // entry does (narrowed to the entries that match) — so typing "ortho" keeps
  // the whole family, and typing "รีเทนเนอร์" keeps just that row under it.
  const filtered = useMemo(() => groups.map((g) => {
    if (!needle) return g
    if (g.type.toLowerCase().includes(needle)) return g
    const items = g.items.filter((it) => it.label.toLowerCase().includes(needle))
    return items.length ? { ...g, items } : null
  }).filter(Boolean), [groups, needle])

  const showGap = gapCount > 0 && (!needle || NO_APPT_LABEL.toLowerCase().includes(needle))

  // One flat list of the selectable rows, in render order, for keyboard nav.
  const flat = useMemo(() => {
    const out = [{ value: '', label: 'ทุกประเภท' }]
    for (const g of filtered) {
      out.push({ value: g.value, label: g.type })
      for (const it of g.items) out.push({ value: it.value, label: it.label })
    }
    if (showGap) out.push({ value: NO_APPT, label: NO_APPT_LABEL })
    return out
  }, [filtered, showGap])

  const pick = (v) => { onChange(v); setOpen(false); setQ('') }
  const ta = useTypeahead({
    count: flat.length,
    onSelect: (i) => flat[i] && pick(flat[i].value),
    onClose: () => setOpen(false),
  })

  const current = flat.find((o) => o.value === value)
  // The selected row may be filtered out of view; fall back to scanning all groups.
  const label = value === ''
    ? 'ทุกประเภท'
    : (current?.label
      || (value === NO_APPT ? NO_APPT_LABEL : null)
      || groups.flatMap((g) => [{ value: g.value, label: g.type }, ...g.items]).find((o) => o.value === value)?.label
      || value)

  let idx = 0
  const rowCls = (i, on) => `flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-slate-50 ${i === ta.activeIndex ? 'bg-slate-100' : ''} ${on ? 'text-brand-600 font-medium' : 'text-slate-700'}`

  return (
    <div className={`relative ${className}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`${INPUT} inline-flex w-full items-center justify-between gap-2 text-left`}>
        <span className={`truncate ${value === '' ? 'text-slate-500' : 'text-slate-700'}`}>{label}</span>
        <span className="flex items-center gap-1 shrink-0">
          {value !== '' && (
            <span
              role="button" tabIndex={-1} title="ล้างตัวกรอง"
              onClick={(e) => { e.stopPropagation(); onChange('') }}
              className="text-slate-300 hover:text-slate-500"
            ><X size={13} /></span>
          )}
          <ChevronDown size={14} className="text-slate-400" />
        </span>
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} aria-hidden tabIndex={-1} />
          <div className="absolute z-20 mt-1 w-full min-w-[260px] rounded-xl border border-slate-200 bg-white shadow-lg p-2">
            <div className="flex items-center gap-1.5 mb-1.5 rounded-md border border-slate-200 px-2 focus-within:border-brand-300 focus-within:ring-2 focus-within:ring-brand-100">
              <Search size={13} className="text-slate-400 shrink-0" />
              <input
                ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={ta.onKeyDown}
                placeholder="ค้นหาประเภท หรือ รายละเอียด…" className="w-full py-1 text-[12.5px] bg-transparent focus:outline-none"
              />
            </div>
            <div ref={ta.listRef} className="max-h-72 overflow-y-auto -mr-1 pr-1">
              {(() => {
                const rows = []
                const all = idx++
                rows.push(
                  <button key="__all" onClick={() => pick('')} data-ti-index={all} onMouseEnter={() => ta.setActiveIndex(all)} className={rowCls(all, value === '')}>
                    ทุกประเภท
                  </button>,
                )
                for (const g of filtered) {
                  const gi = idx++
                  rows.push(
                    <button key={g.value} onClick={() => pick(g.value)} data-ti-index={gi} onMouseEnter={() => ta.setActiveIndex(gi)} className={`${rowCls(gi, value === g.value)} mt-1 font-semibold`}>
                      <span className="truncate">{g.type}</span>
                      <span className="ml-auto text-[11px] text-slate-400 shrink-0">{fmt(g.count)}</span>
                    </button>,
                  )
                  for (const it of g.items) {
                    const ii = idx++
                    rows.push(
                      <button key={it.value} onClick={() => pick(it.value)} data-ti-index={ii} onMouseEnter={() => ta.setActiveIndex(ii)} className={`${rowCls(ii, value === it.value)} pl-5`}>
                        <span className="truncate text-slate-600">{it.label}</span>
                        <span className="ml-auto text-[11px] text-slate-400 shrink-0">{fmt(it.count)}</span>
                      </button>,
                    )
                  }
                }
                if (showGap) {
                  const gi = idx++
                  rows.push(
                    <button key={NO_APPT} onClick={() => pick(NO_APPT)} data-ti-index={gi} onMouseEnter={() => ta.setActiveIndex(gi)} className={`${rowCls(gi, value === NO_APPT)} mt-1`}>
                      <span className="truncate text-amber-600">{NO_APPT_LABEL}</span>
                      <span className="ml-auto text-[11px] text-slate-400 shrink-0">{fmt(gapCount)}</span>
                    </button>,
                  )
                }
                if (rows.length === 1) rows.push(<div key="__none" className="px-2 py-3 text-center text-[12px] text-slate-400">ไม่พบ</div>)
                return rows
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function SumTile({ label, value, color, strong, muted }) {
  return (
    <div className="min-w-[96px]">
      <div className="flex items-center gap-1.5 text-[11.5px] text-slate-500">
        {color && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}{label}
      </div>
      <div className={`tabular-nums leading-tight ${strong ? 'text-[18px] font-semibold text-slate-900' : `text-[15px] font-medium ${muted ? 'text-slate-400' : 'text-slate-700'}`}`}>{value}</div>
    </div>
  )
}

function GroupBars({ rows, total }) {
  const max = Math.max(1, ...rows.map((r) => r.count))
  const top = rows.slice(0, 12)
  return (
    <div className="space-y-2.5">
      {top.map((r) => (
        <div key={r.key} className="flex items-center gap-3">
          <div className="w-44 shrink-0 truncate text-[12.5px] text-slate-700" title={r.label}>{r.label}</div>
          <div className="flex-1 h-6 rounded-md bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-md transition-all"
              style={{ width: `${Math.max(2, (r.count / max) * 100)}%`, background: r.key === NO_APPT ? BAR_GAP : BAR }}
            />
          </div>
          <div className="w-16 shrink-0 text-right tabular-nums text-[12.5px] font-semibold text-slate-800">{fmt(r.count)}</div>
          <div className="w-14 shrink-0 text-right tabular-nums text-[12px] text-slate-400">{total ? `${Math.round((r.count / total) * 100)}%` : '—'}</div>
        </div>
      ))}
      {rows.length > top.length && <div className="text-[12px] text-slate-400 pt-1">และอีก {fmt(rows.length - top.length)} กลุ่มด้านล่าง…</div>}
    </div>
  )
}

// The new HNs inside one group.
function CaseList({ rows, dim }) {
  const sorted = [...rows].sort((a, b) => a.createdMs - b.createdMs)
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-2 overflow-x-auto">
      <table className="w-full text-[12.5px] table-fixed min-w-[860px]">
        <colgroup>
          <col className="w-[96px]" /><col className="w-[92px]" /><col /><col className="w-[104px]" />
          <col className="w-[110px]" /><col className="w-[168px]" /><col className="w-[96px]" /><col className="w-[46px]" />
        </colgroup>
        <thead>
          <tr className="text-[11px] text-slate-400 border-b border-slate-100">
            <th className="text-left font-medium py-1">เปิด HN</th>
            <th className="text-left font-medium">HN</th>
            <th className="text-left font-medium">ชื่อ</th>
            <th className="text-left font-medium">โทร</th>
            {dim === 'branch' ? <th className="text-left font-medium">ที่มา</th> : <th className="text-left font-medium">สาขา</th>}
            <th className="text-left font-medium">นัดหมาย</th>
            <th className="text-left font-medium">สถานะนัด</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const st = apptStatusInfo(r.apptStatus)
            return (
              <tr key={r.patientId} className="border-b border-slate-50 last:border-0 align-top">
                <td className="py-1.5 text-slate-500 tabular-nums whitespace-nowrap">
                  {fmtDay(r.createdDay)}<span className="text-slate-300 ml-1">{r.createdTime}</span>
                </td>
                <td className="pr-2 tabular-nums text-slate-600">{r.hn || '—'}</td>
                <td className="pr-2 truncate">
                  {r.name}
                  {r.nickname ? <span className="text-slate-400 ml-1.5 text-[11px]">({r.nickname})</span> : null}
                </td>
                <td className="pr-2 tabular-nums text-slate-600">{r.phone || '—'}</td>
                <td className="pr-2 truncate text-slate-600">{dim === 'branch' ? r.apptFrom : r.branchName}</td>
                <td className="pr-2">
                  {r.hasAppt ? (
                    <>
                      <span className="tabular-nums text-slate-700">{fmtDay(r.apptDate)}</span>
                      <span className="text-slate-400 ml-1.5">{r.typeMain}</span>
                      {r.typeDetail ? <div className="text-[11px] text-slate-400 truncate">{r.typeDetail}</div> : null}
                      {r.extraAppts > 0 ? <div className="text-[11px] text-brand-500">+{r.extraAppts} นัด</div> : null}
                    </>
                  ) : (
                    <span className="text-amber-600">{NO_APPT_LABEL}</span>
                  )}
                </td>
                <td className="pr-2">
                  {r.hasAppt
                    ? <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] ring-1 ring-inset ${st.pill}`}>{st.abbr}</span>
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="text-right">
                  {r.patientId ? (
                    <a
                      href={`/patient/${r.patientId}`} target="_blank" rel="noopener noreferrer" title="เปิดหน้าคนไข้"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-1.5 py-1 text-[11px] text-slate-500 hover:border-brand-300 hover:text-brand-600"
                    >
                      <ExternalLink size={12} />
                    </a>
                  ) : null}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
export default function NewCase() {
  // Bangkok's today, not the browser's — the window is resolved in Bangkok
  // upstream, so a viewer in another timezone must not be offered a day the
  // query would refuse (or defaulted to yesterday's clinic day).
  const today = useMemo(() => bkkTodayISO(), [])

  const [start, setStart] = usePersistedState('nc:start', today)
  const [end, setEnd] = usePersistedState('nc:end', today)
  const [branchSel, setBranchSel] = usePersistedState('nc:branch', '')
  const [typeSel, setTypeSel] = usePersistedState('nc:type', '')
  const [groupByRaw, setGroupBy] = usePersistedState('nc:groupBy', 'type')
  // A persisted value from an older build could name an axis that no longer
  // exists; fall back rather than throwing on GROUPERS[undefined].
  const groupBy = GROUPERS[groupByRaw] ? groupByRaw : 'type'

  const [branches, setBranches] = useState({})
  const [types, setTypes] = useState({})
  const [raw, setRaw] = useState(null)
  const [st, setSt] = useState({ loading: true, err: '' })
  const [openGroup, setOpenGroup] = useState(null)

  // Catalogs. Both are memoized by lib/api.js, so this is one fetch per session.
  useEffect(() => {
    cachedGet('/api/branches')
      .then((r) => {
        const m = {}
        for (const b of asList(r)) m[String(b.id ?? b.branch_id)] = b.name || b.branch_name || ''
        setBranches(m)
      })
      .catch(() => {})
    cachedGet('/api/appointment-types')
      .then((r) => {
        const m = {}
        for (const t of asList(r)) {
          const id = String(t.id ?? '')
          if (!id) continue
          m[id] = { type: String(t.type || '').trim() || '—', detail: String(t.detail || '').trim() }
        }
        setTypes(m)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    let alive = true
    setSt({ loading: true, err: '' })
    setOpenGroup(null)
    apiGet('/api/newcase', { start_date: start, end_date: end })
      .then((r) => { if (alive) { setRaw(r); setSt({ loading: false, err: '' }) } })
      // Xano answers {code, message, payload}; apiErrorText digs the Thai
      // sentence out of the `HTTP 400: {json}` wrapper apiGet throws.
      .catch((e) => { if (alive) { setRaw(null); setSt({ loading: false, err: apiErrorText(e, 'โหลดข้อมูลไม่สำเร็จ') }) } })
    return () => { alive = false }
  }, [start, end])

  const allRows = useMemo(() => {
    if (!raw) return []
    return buildRows(asList(raw.patients), asList(raw.appointments), types, branches)
  }, [raw, types, branches])

  // Filter options come from what is actually in the window, not the full catalogs.
  const branchOpts = useMemo(() => {
    const m = new Map()
    for (const r of allRows) if (!m.has(r.branchId)) m.set(r.branchId, r.branchName)
    return [...m.entries()].sort((a, b) => Number(a[0]) - Number(b[0]))
  }, [allRows])

  // Grouped ประเภทนัดหมาย options, and how many cases have no appointment at
  // all — both scoped to the branch filter, so the counts in the dropdown match
  // what picking that option would actually show.
  const typeScope = useMemo(
    () => (branchSel ? allRows.filter((r) => r.branchId === branchSel) : allRows),
    [allRows, branchSel],
  )
  const typeGroups = useMemo(() => typeOptions(typeScope), [typeScope])
  const gapCount = useMemo(() => typeScope.reduce((n, r) => n + (r.hasAppt ? 0 : 1), 0), [typeScope])

  const rows = useMemo(
    () => filterRows(allRows, { branchId: branchSel, type: typeSel }),
    [allRows, branchSel, typeSel],
  )

  useEffect(() => { setOpenGroup(null) }, [groupBy, branchSel, typeSel])

  const withAppt = useMemo(() => rows.reduce((n, r) => n + (r.hasAppt ? 1 : 0), 0), [rows])
  const groupRows = useMemo(() => pivot(rows, groupBy), [rows, groupBy])
  const noun = GROUPERS[groupBy].noun

  return (
    <div className="px-8 py-6 max-w-[1180px]">
      {/* header */}
      <div className="flex items-center gap-2 text-[13px] text-slate-400 mb-1">
        <Link to="/reports" className="hover:text-brand-600">รายงาน</Link><span>/</span><span className="text-slate-600">เคสนัดใหม่ประจำวัน</span>
      </div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><UserPlus size={22} strokeWidth={1.75} /></div>
        <div>
          <h1 className="text-[21px] font-semibold text-slate-900 tracking-tight leading-tight">เคสนัดใหม่ประจำวัน</h1>
          <p className="text-[13px] text-slate-500">HN ที่เปิดใหม่ในช่วงวันที่ · พร้อมนัดหมายของคนไข้คนนั้น · อ่านอย่างเดียว</p>
        </div>
      </div>

      {/* filter bar */}
      <Card icon={<Search size={18} />} title="ค้นหา / กรอง" subtitle="ช่วงวันที่ (วันที่เปิด HN · เวลาไทย) · สาขา · ประเภทนัดหมาย · จัดกลุ่มตาม">
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">ช่วงวันที่</label>
            {/* 60 days, not the endpoint's 92. One day is ~70 new HNs and all of
                their appointments, and the whole window comes down in ONE payload
                that the page then filters and groups client-side — so the window
                is also the request size. 92 was the upstream precondition's
                number, never a considered UI limit; the server keeps it as the
                backstop. See functions/api/newcase.js. */}
            <DateRange start={start} end={end} today={today} onStart={setStart} onEnd={setEnd} maxDays={NEWCASE_MAX_DAYS} />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">สาขา</label>
            <Select value={branchSel} onChange={setBranchSel} className="min-w-[150px]">
              <option value="">ทุกสาขา</option>
              {branchOpts.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </Select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">ประเภทนัดหมาย</label>
            <TypeSelect value={typeSel} onChange={setTypeSel} groups={typeGroups} gapCount={gapCount} className="w-[230px]" />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">จัดกลุ่มตาม</label>
            <Segmented value={groupBy} onChange={setGroupBy} options={GROUP_OPTS} />
          </div>
        </div>
      </Card>

      {st.err ? (
        <div className="mt-4"><Notice tone="error" text={`โหลดข้อมูลไม่สำเร็จ: ${st.err}`} /></div>
      ) : st.loading ? (
        <div className="mt-4">
          <Card icon={<UserPlus size={18} />} title="กำลังโหลด…">
            <div className="flex items-center justify-center gap-2 text-[13px] text-slate-400 h-[200px]"><Loader2 size={16} className="animate-spin" /> กำลังโหลดเคสนัดใหม่…</div>
          </Card>
        </div>
      ) : (
        <>
          {/* summary */}
          <div className="mt-4">
            <Card
              icon={<UserPlus size={18} />}
              title="สรุปเคสนัดใหม่"
              subtitle="นับจากวันที่เปิด HN (created_at, เวลาไทย) — 1 เคส = 1 HN ใหม่"
            >
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                <SumTile label="เคสใหม่ทั้งหมด" value={`${fmt(rows.length)} HN`} strong />
                <SumTile label="มีนัดหมายแล้ว" value={`${fmt(withAppt)} เคส`} color={BAR} />
                <SumTile label={NO_APPT_LABEL} value={`${fmt(rows.length - withAppt)} เคส`} muted={rows.length === withAppt} />
                <SumTile label={noun} value={`${fmt(groupRows.length)} กลุ่ม`} />
              </div>
            </Card>
          </div>

          {!rows.length ? (
            <div className="mt-4"><Notice tone="warn" text="ไม่พบเคสนัดใหม่ในช่วงและตัวกรองที่เลือก" /></div>
          ) : (
            <>
              {/* chart */}
              <div className="mt-4">
                <Card icon={<CalendarClock size={18} />} title={`เคสใหม่แยกตาม${noun}`} badge={`${fmt(groupRows.length)} กลุ่ม`} subtitle={groupBy === 'date' ? 'เรียงตามวันที่' : 'เรียงจากมากไปน้อย'}>
                  <GroupBars rows={groupRows} total={rows.length} />
                </Card>
              </div>

              {/* grouped table + drill-down */}
              <div className="mt-4">
                <Card icon={<UserPlus size={18} />} title={`รายละเอียดแยกตาม${noun}`} subtitle="กดที่แถวเพื่อดูรายชื่อเคสใหม่ในกลุ่มนั้น">
                  <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full text-[13px] table-fixed">
                      <colgroup><col className="w-[34px]" /><col /><col className="w-[120px]" /><col className="w-[110px]" /></colgroup>
                      <thead>
                        <tr className="text-[11.5px] text-slate-400 border-b border-slate-200">
                          <th></th>
                          <th className="text-left font-medium py-2">{noun}</th>
                          <th className="text-right font-medium">จำนวน (เคส)</th>
                          <th className="text-right font-medium">สัดส่วน</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupRows.map((row) => {
                          const open = openGroup === row.key
                          return (
                            <Fragment key={row.key}>
                              <tr className={`border-b border-slate-100 cursor-pointer ${open ? 'bg-brand-50/40' : 'hover:bg-slate-50/70'}`} onClick={() => setOpenGroup(open ? null : row.key)}>
                                <td className="py-2.5 text-slate-300"><ChevronDown size={16} className={`transition ${open ? '' : '-rotate-90'}`} /></td>
                                <td className="pr-2">
                                  <span className={`truncate font-medium ${row.key === NO_APPT ? 'text-amber-600' : 'text-slate-800'}`}>{row.label}</span>
                                </td>
                                <td className="text-right tabular-nums font-semibold text-slate-800">{fmt(row.count)}</td>
                                <td className="text-right tabular-nums text-slate-500">{Math.round((row.count / rows.length) * 100)}%</td>
                              </tr>
                              {open && (
                                <tr>
                                  <td></td>
                                  <td colSpan={3} className="py-2 pr-1"><CaseList rows={row.rows} dim={groupBy} /></td>
                                </tr>
                              )}
                            </Fragment>
                          )
                        })}
                        <tr className="border-t border-slate-200">
                          <td></td>
                          <td className="py-2 text-[12.5px] font-medium text-slate-500">รวม</td>
                          <td className="text-right tabular-nums font-semibold text-slate-900">{fmt(rows.length)}</td>
                          <td className="text-right tabular-nums text-slate-400">100%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
